
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import ImageUpload from '@/components/shared/ImageUpload'

interface TeamMember {
  id: string
  name: string
  position: string
  bio: string | null
  image_url: string | null
  email: string | null
  linkedin_url: string | null
  display_order: number
  is_active: boolean
}

interface TeamFormProps {
  teamMember?: TeamMember | null
  onSave: () => void
  onCancel: () => void
}

const TeamForm = ({ teamMember, onSave, onCancel }: TeamFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    email: '',
    linkedin_url: '',
    display_order: 0,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (teamMember) {
      setFormData({
        name: teamMember.name,
        position: teamMember.position,
        bio: teamMember.bio || '',
        image_url: teamMember.image_url || '',
        email: teamMember.email || '',
        linkedin_url: teamMember.linkedin_url || '',
        display_order: teamMember.display_order,
        is_active: teamMember.is_active
      })
    }
  }, [teamMember])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const teamData = {
        name: formData.name,
        position: formData.position,
        bio: formData.bio || null,
        image_url: formData.image_url || null,
        email: formData.email || null,
        linkedin_url: formData.linkedin_url || null,
        display_order: formData.display_order,
        is_active: formData.is_active
      }

      if (teamMember) {
        const { error } = await supabase
          .from('team_members')
          .update(teamData)
          .eq('id', teamMember.id)
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Team member updated successfully",
        })
      } else {
        const { error } = await supabase
          .from('team_members')
          .insert([teamData])
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Team member created successfully",
        })
      }
      
      onSave()
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${teamMember ? 'update' : 'create'} team member`,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{teamMember ? 'Edit Team Member' : 'Add New Team Member'}</CardTitle>
        <CardDescription>
          {teamMember ? 'Update team member information' : 'Create a new team member entry'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="position">Position</Label>
            <Input
              id="position"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="linkedin_url">LinkedIn URL</Label>
            <Input
              id="linkedin_url"
              value={formData.linkedin_url}
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
            />
          </div>

          <ImageUpload
            currentImageUrl={formData.image_url}
            onImageUpload={(url) => setFormData({ ...formData, image_url: url })}
            onImageRemove={() => setFormData({ ...formData, image_url: '' })}
            label="Team Member Photo"
            folder="team"
          />
          
          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : (teamMember ? 'Update Team Member' : 'Create Team Member')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default TeamForm
